import { FC, useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useUploadAssetHook } from "../../../hooks/useBrandingHook";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import { TBrandingAssetType } from "../../../apis/branding/branding.interfaces";

interface AssetUploadDialogProps {
  open: boolean;
  onClose: () => void;
  assetType: TBrandingAssetType;
  existingAsset?: { fileName: string; fileUrl: string };
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ASSET_TYPE_CONFIG: Record<
  TBrandingAssetType,
  { label: string; supportedFormats: string[]; description: string }
> = {
  favicon: {
    label: "Favicon",
    supportedFormats: [
      "image/png",
      "image/x-icon",
      "image/vnd.microsoft.icon",
      "image/svg+xml",
    ],
    description:
      "Small icon displayed in browser tabs (recommended: 32x32 or 16x16 pixels)",
  },
  emailTemplate: {
    label: "Email Template",
    supportedFormats: ["text/html", "application/pdf"],
    description: "HTML or PDF template for branded emails",
  },
  documentTemplate: {
    label: "Document Template",
    supportedFormats: [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
    ],
    description: "PDF or Word document template for branded documents",
  },
};

const AssetUploadDialog: FC<AssetUploadDialogProps> = ({
  open,
  onClose,
  assetType,
  existingAsset,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const uploadAssetMutation = useUploadAssetHook();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const config = ASSET_TYPE_CONFIG[assetType];

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setValidationError(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const validateFile = (file: File): string | null => {
    if (!config.supportedFormats.includes(file.type)) {
      return `Unsupported file format. Please upload ${config.supportedFormats.map((f) => f.split("/")[1]).join(", ")} files only.`;
    }

    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      setSelectedFile(null);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedInstitute) {
      return;
    }

    try {
      await uploadAssetMutation.mutateAsync({
        request: {
          instituteId: selectedInstitute.id,
          assetType,
          file: selectedFile,
        },
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      logger.show_success(`${config.label} uploaded successfully!`);
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to upload ${config.label.toLowerCase()}. Please try again.`,
      );
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const getAcceptString = () => {
    return config.supportedFormats.join(",");
  };

  const isUploading = uploadAssetMutation.isPending;
  const hasFile = !!selectedFile;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      data-testid="asset-upload-dialog"
    >
      <DialogTitle>
        {existingAsset ? `Replace ${config.label}` : `Upload ${config.label}`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {validationError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="asset-validation-error"
            >
              {validationError}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {config.description}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Supported formats:{" "}
              {config.supportedFormats
                .map((f) => f.split("/")[1].toUpperCase())
                .join(", ")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Maximum file size: {MAX_FILE_SIZE / (1024 * 1024)}MB
            </Typography>
          </Box>

          {existingAsset && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Current file: {existingAsset.fileName}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptString()}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              data-testid="asset-file-input"
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
              disabled={isUploading}
              data-testid="select-asset-button"
            >
              {hasFile ? "Select Different File" : "Select File"}
            </Button>
          </Box>

          {hasFile && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected File: {selectedFile?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                File size: {(selectedFile?.size || 0) / (1024 * 1024)}MB
              </Typography>
            </Box>
          )}

          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading... {uploadProgress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                data-testid="asset-upload-progress"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isUploading}
          startIcon={<CancelIcon />}
          data-testid="asset-upload-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!hasFile || isUploading || !!validationError}
          startIcon={<CheckCircleIcon />}
          data-testid="asset-upload-confirm-button"
        >
          {existingAsset ? "Replace" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetUploadDialog;
